/**
 * useCurrency Hook
 *
 * Tiered currency detection:
 *   1. Session override (user manually toggled currency this session)
 *   2. (Future) Registered user account setting
 *   3. IP-based geolocation via PHP endpoint (most accurate, no permission needed)
 *   4. Browser locale heuristic (navigator.language / timezone) as fallback
 *   5. Default: USD
 *
 * When a user manually changes currency, it persists for the current
 * session only (sessionStorage), overriding all detection methods.
 */

import { useState, useEffect, useCallback } from 'react';
import { Currency } from '../types';

const SESSION_KEY = 'trusscore_currency_override';
const DETECTED_KEY = 'trusscore_currency_detected';

interface UseCurrencyReturn {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  isDetecting: boolean;
  detectionSource: string;
}

/**
 * Tier 4: Browser locale heuristic (instant, no permissions)
 */
function detectFromLocale(): Currency | null {
  try {
    const language = navigator.language || (navigator as any).userLanguage || '';
    const isCanadian = language.toLowerCase().includes('ca');

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const canadianZones = [
      'Canada', 'Toronto', 'Vancouver', 'Edmonton', 'Winnipeg',
      'Halifax', 'Regina', 'St_Johns', 'Whitehorse', 'Yellowknife',
      'Iqaluit', 'Montreal', 'Moncton', 'Thunder_Bay', 'Nipigon',
      'Rainy_River', 'Atikokan', 'Rankin_Inlet', 'Resolute',
      'Swift_Current', 'Cambridge_Bay', 'Inuvik', 'Creston',
      'Dawson_Creek', 'Fort_Nelson', 'Dawson', 'Glace_Bay', 'Goose_Bay',
      'Pangnirtung', 'Blanc-Sablon',
    ];
    const isCanadianTimezone = canadianZones.some(z => timezone.includes(z));

    if (isCanadian || isCanadianTimezone) {
      return 'CAD';
    }
    return null; // Inconclusive — don't assume USD yet
  } catch {
    return null;
  }
}

/**
 * Tier 3: IP-based geolocation via PHP endpoint (most accurate)
 */
async function detectFromIP(): Promise<Currency | null> {
  try {
    const apiBase = window.location.hostname === 'localhost'
      ? 'http://chriscme/api'
      : '/api';
    const res = await fetch(`${apiBase}/geolocate.php`, {
      signal: AbortSignal.timeout(5000),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.currency === 'USD' || data.currency === 'CAD') {
        return data.currency;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Run full tiered detection
 */
async function detectCurrency(): Promise<{ currency: Currency; source: string }> {
  // Tier 3: IP geolocation (most accurate, no permission needed)
  const ipCurrency = await detectFromIP();
  if (ipCurrency) {
    return { currency: ipCurrency, source: 'ip' };
  }

  // Tier 4: Browser locale fallback (if IP fails due to network/timeout)
  const localeCurrency = detectFromLocale();
  if (localeCurrency) {
    return { currency: localeCurrency, source: 'locale' };
  }

  // Tier 5: Default
  return { currency: 'USD', source: 'default' };
}

/**
 * Custom hook for managing currency with tiered auto-detection.
 */
export function useCurrency(): UseCurrencyReturn {
  // Check session override first (tier 1)
  const sessionOverride = sessionStorage.getItem(SESSION_KEY);
  const initialCurrency: Currency =
    sessionOverride === 'USD' || sessionOverride === 'CAD'
      ? sessionOverride
      : 'USD';

  const [currency, setCurrencyState] = useState<Currency>(initialCurrency);
  const [isDetecting, setIsDetecting] = useState(!sessionOverride);
  const [detectionSource, setDetectionSource] = useState(
    sessionOverride ? 'session' : 'pending'
  );

  // Run tiered detection on mount (only if no session override)
  useEffect(() => {
    if (sessionOverride) return;

    // Check if we already detected this session
    const cached = sessionStorage.getItem(DETECTED_KEY);
    if (cached === 'USD' || cached === 'CAD') {
      setCurrencyState(cached);
      setDetectionSource('cached');
      setIsDetecting(false);
      return;
    }

    let cancelled = false;

    detectCurrency().then(({ currency: detected, source }) => {
      if (cancelled) return;
      setCurrencyState(detected);
      setDetectionSource(source);
      setIsDetecting(false);
      sessionStorage.setItem(DETECTED_KEY, detected);
    }).catch(() => {
      setIsDetecting(false);
    });

    return () => { cancelled = true; };
  }, []);

  // Manual currency change — persists for session, overrides all detection
  const setCurrency = useCallback((newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    setDetectionSource('manual');
    sessionStorage.setItem(SESSION_KEY, newCurrency);
  }, []);

  return {
    currency,
    setCurrency,
    isDetecting,
    detectionSource,
  };
}

export default useCurrency;
